import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { Job, JobAttempt, JobItem } from '@/api/types';
import {
  canCancelJob,
  canCancelJobItem,
  canRetryJob,
  canRetryJobItem,
  formatJobItemType,
  formatJobType,
  isTerminalJobStatus,
  jobItemStatusLabel,
  jobStatusLabel,
  jobStatusVariant,
  shortId,
} from '@/utils/jobStatus';
import {
  buildSendJobFailureSummary,
  isFailedJobItemStatus,
  jobItemSourceName,
} from '@/utils/jobItemErrors';
import { resolveJobRetryId } from '@/utils/jobRetryIds';
import StatusBadge from '@/components/ui/StatusBadge';
import SourceChips from '@/components/ui/SourceChips';
import ExportButtons from '@/components/ui/ExportButtons';
import JobStatusFilterSelect from './JobStatusFilterSelect';
import ActivityLogPanel from '@/components/activity/ActivityLogPanel';
import OperationalErrorCell from './OperationalErrorCell';
import JobItemDetailPanel from './JobItemDetailPanel';
import SendJobFailureSummaryPanel from './SendJobFailureSummary';
import { useToast } from '@/components/Toast';

type Props = {
  jobId: string;
  job: Job | null;
  items: JobItem[];
  attempts: JobAttempt[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onRefresh: (itemStatus?: string, attemptStatus?: string) => Promise<unknown>;
  onActionComplete: () => Promise<void>;
  onItemStatusFilterChange: (status: string) => void;
  onAttemptStatusFilterChange: (status: string) => void;
  canManageJobs?: boolean;
  onClearActivity?: (jobId: string) => void;
};

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatDuration(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

const ITEM_STATUS_OPTIONS = [
  { value: '', label: 'All item statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead_letter', label: 'Dead letter' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ATTEMPT_STATUS_OPTIONS = [
  { value: '', label: 'All attempt statuses' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

function isSendJob(job: Job | null): boolean {
  return job?.type === 'send_batch';
}

export default function JobDetailDrawer({
  jobId,
  job,
  items,
  attempts,
  loading,
  error,
  onClose,
  onRefresh,
  onActionComplete,
  onItemStatusFilterChange,
  onAttemptStatusFilterChange,
  canManageJobs = false,
  onClearActivity,
}: Props) {
  const { toast } = useToast();
  const [itemStatusFilter, setItemStatusFilter] = useState('');
  const [attemptStatusFilter, setAttemptStatusFilter] = useState('');
  const [actionPending, setActionPending] = useState<'cancel' | 'retry' | null>(null);
  const [itemActionId, setItemActionId] = useState<string | null>(null);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  useEffect(() => {
    setItemStatusFilter('');
    setAttemptStatusFilter('');
    setExpandedItemId(null);
  }, [jobId]);

  const sendFailureSummary = useMemo(() => {
    if (!job || !isSendJob(job)) return null;
    return buildSendJobFailureSummary(items);
  }, [job, items]);

  const progress =
    job && job.total_items > 0 ? Math.min(100, Math.max(0, job.progress_percent)) : 0;
  const actionJobId = resolveJobRetryId(job, jobId);

  async function handleCancel() {
    if (!job || !canCancelJob(job.status)) return;
    setActionPending('cancel');
    try {
      await api.jobs.cancel(actionJobId);
      await onActionComplete();
      toast('Job cancelled', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Cancel failed', 'error');
    } finally {
      setActionPending(null);
    }
  }

  async function handleRetry() {
    if (!job || !canRetryJob(job.status)) return;
    setActionPending('retry');
    try {
      await api.jobs.retry(actionJobId);
      await onActionComplete();
      toast('Job requeued for retry', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Retry failed', 'error');
    } finally {
      setActionPending(null);
    }
  }

  async function handleItemFilterChange(value: string) {
    setItemStatusFilter(value);
    onItemStatusFilterChange(value);
    await onRefresh(value, attemptStatusFilter);
  }

  async function handleItemRetry(itemId: string) {
    setItemActionId(itemId);
    try {
      const result = await api.jobs.retryItem(actionJobId, itemId);
      if ((result.failed ?? 0) > 0) {
        toast('Item could not be retried', 'error');
      } else {
        toast('Item requeued for retry', 'success');
      }
      await onActionComplete();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Retry failed', 'error');
    } finally {
      setItemActionId(null);
    }
  }

  async function handleItemCancel(itemId: string) {
    setItemActionId(itemId);
    try {
      const result = await api.jobs.cancelItem(actionJobId, itemId);
      if ((result.failed ?? 0) > 0) {
        toast('Item could not be cancelled', 'error');
      } else {
        toast('Item cancelled', 'info');
      }
      await onActionComplete();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Cancel failed', 'error');
    } finally {
      setItemActionId(null);
    }
  }

  async function handleAttemptFilterChange(value: string) {
    setAttemptStatusFilter(value);
    onAttemptStatusFilterChange(value);
    await onRefresh(itemStatusFilter, value);
  }

  return (
    <>
      <button type="button" className="job-drawer-backdrop" aria-label="Close job details" onClick={onClose} />
      <aside className="job-detail-drawer" aria-label="Job details">
        <header className="job-detail-drawer__header">
          <div>
            <h2>Job details</h2>
            <p className="muted job-detail-drawer__id-label">Job ID</p>
            <p className="muted mono-id" title={actionJobId}>
              {actionJobId}
            </p>
            {job && job.id !== jobId ? (
              <p className="muted job-detail-drawer__id-note">
                Loaded job id differs from selection — actions use {shortId(job.id)}.
              </p>
            ) : null}
          </div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            Close
          </button>
        </header>

        {loading && !job && (
          <div className="loading-inline" role="status">
            <span className="spinner" aria-hidden />
            Loading job…
          </div>
        )}

        {error && <p className="error-text">{error}</p>}

        {job && (
          <>
            <div className="job-detail-drawer__meta">
              <div className="job-detail-drawer__meta-row">
                <StatusBadge variant={jobStatusVariant(job.status)}>{jobStatusLabel(job.status)}</StatusBadge>
                <span className="muted">{formatJobType(job.type)}</span>
              </div>
              <div className="job-progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                <div className="job-progress-bar__fill" style={{ width: `${progress}%` }} />
              </div>
              <p className="job-progress-card__counts">
                {job.processed_items} / {job.total_items} processed
                {job.failed_items > 0 && (
                  <span className="job-progress-card__failed"> · {job.failed_items} failed</span>
                )}
              </p>
              <dl className="job-progress-card__meta">
                <div>
                  <dt>Created</dt>
                  <dd>{formatTime(job.created_at)}</dd>
                </div>
                <div>
                  <dt>Started</dt>
                  <dd>{formatTime(job.started_at)}</dd>
                </div>
                <div>
                  <dt>Finished</dt>
                  <dd>{formatTime(job.finished_at)}</dd>
                </div>
                {job.search_id && (
                  <div>
                    <dt>Search</dt>
                    <dd className="mono-id" title={job.search_id}>
                      {shortId(job.search_id)}
                    </dd>
                  </div>
                )}
                {job.subscription_id && (
                  <div>
                    <dt>Subscription</dt>
                    <dd className="mono-id" title={job.subscription_id}>
                      {shortId(job.subscription_id)}
                    </dd>
                  </div>
                )}
              </dl>
              {job.error && (
                <p className="job-progress-card__error" role="alert">
                  <OperationalErrorCell value={job.error} maxWidth="100%" copyable />
                </p>
              )}
            </div>

            <div className="job-detail-drawer__actions">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => onRefresh(itemStatusFilter, attemptStatusFilter)}
              >
                Refresh
              </button>
              {canCancelJob(job.status) && (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={actionPending !== null}
                  onClick={handleCancel}
                >
                  {actionPending === 'cancel' ? 'Cancelling…' : 'Cancel'}
                </button>
              )}
              {canRetryJob(job.status) && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={actionPending !== null}
                  onClick={handleRetry}
                >
                  {actionPending === 'retry' ? 'Retrying…' : 'Retry'}
                </button>
              )}
              {!isTerminalJobStatus(job.status) && (
                <span className="muted job-detail-drawer__live">Live updates</span>
              )}
            </div>

            {sendFailureSummary ? <SendJobFailureSummaryPanel summary={sendFailureSummary} /> : null}

            <section className="job-detail-drawer__section">
              <div className="job-detail-drawer__section-header">
                <h3>Job items</h3>
                <div className="job-detail-drawer__section-actions">
                  <ExportButtons
                    path={`/exports/jobs/${jobId}/items`}
                    params={{ status: itemStatusFilter || undefined }}
                  />
                  <JobStatusFilterSelect
                  label="Item status"
                  htmlFor="job-items-status-filter"
                  value={itemStatusFilter}
                  options={ITEM_STATUS_OPTIONS}
                  onChange={(value) => void handleItemFilterChange(value)}
                />
                </div>
              </div>
              <div className="table-scroll">
                <table className="data-table job-items-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Item key</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Attempts</th>
                      <th>Lease owner</th>
                      <th>Lease expires</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>Error</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="muted">
                          No items match this filter.
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <React.Fragment key={item.id}>
                          <tr>
                            <td>
                              {jobItemSourceName(item) ? (
                                <SourceChips sources={[jobItemSourceName(item)!]} variant="compact" />
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="mono-id" title={item.item_key || item.id}>
                              {item.item_key ? shortId(item.item_key, 12) : shortId(item.id, 8)}
                            </td>
                            <td>{formatJobItemType(item.type)}</td>
                            <td>
                              <StatusBadge variant={jobStatusVariant(item.status)}>
                                {jobItemStatusLabel(item.status)}
                              </StatusBadge>
                            </td>
                            <td>
                              {item.attempt_count}/{item.max_attempts}
                            </td>
                            <td className="mono-id">{item.lease_owner || '—'}</td>
                            <td>{formatTime(item.lease_expires_at)}</td>
                            <td>{formatTime(item.started_at)}</td>
                            <td>{formatTime(item.finished_at)}</td>
                            <td>
                              {item.error ? (
                                <OperationalErrorCell value={item.error} maxWidth={200} copyable />
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <div className="table-row-actions">
                                {isFailedJobItemStatus(item.status) ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-ghost"
                                    aria-expanded={expandedItemId === item.id}
                                    onClick={() =>
                                      setExpandedItemId((current) =>
                                        current === item.id ? null : item.id,
                                      )
                                    }
                                  >
                                    {expandedItemId === item.id ? 'Hide' : 'Details'}
                                  </button>
                                ) : null}
                                {canRetryJobItem(item.status) ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-ghost"
                                    disabled={itemActionId === item.id}
                                    onClick={() => void handleItemRetry(item.id)}
                                  >
                                    Retry
                                  </button>
                                ) : null}
                                {canCancelJobItem(item.status) ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-ghost"
                                    disabled={itemActionId === item.id}
                                    onClick={() => void handleItemCancel(item.id)}
                                  >
                                    Cancel
                                  </button>
                                ) : null}
                                {!canRetryJobItem(item.status) &&
                                !canCancelJobItem(item.status) &&
                                !isFailedJobItemStatus(item.status) ? (
                                  <span className="muted">—</span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                          {expandedItemId === item.id ? (
                            <tr className="job-item-detail-row">
                              <td colSpan={11}>
                                <JobItemDetailPanel item={item} />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="job-detail-drawer__section">
              <div className="job-detail-drawer__section-header">
                <h3>Attempts</h3>
                <JobStatusFilterSelect
                  label="Attempt status"
                  htmlFor="job-attempts-status-filter"
                  value={attemptStatusFilter}
                  options={ATTEMPT_STATUS_OPTIONS}
                  onChange={(value) => void handleAttemptFilterChange(value)}
                />
              </div>
              <div className="table-scroll">
                <table className="data-table job-items-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Worker</th>
                      <th>Item</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="muted">
                          No attempts recorded yet.
                        </td>
                      </tr>
                    ) : (
                      attempts.map((attempt) => (
                        <tr key={attempt.id}>
                          <td>{formatTime(attempt.started_at)}</td>
                          <td>{formatTime(attempt.finished_at)}</td>
                          <td>{formatDuration(attempt.duration_ms)}</td>
                          <td>
                            <StatusBadge variant={jobStatusVariant(attempt.status)}>
                              {jobStatusLabel(attempt.status)}
                            </StatusBadge>
                          </td>
                          <td className="mono-id">{attempt.worker_id}</td>
                          <td className="mono-id" title={attempt.job_item_id}>
                            {shortId(attempt.job_item_id)}
                          </td>
                          <td>
                            {attempt.error ? (
                              <OperationalErrorCell value={attempt.error} maxWidth={200} copyable />
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="job-detail-drawer__section">
              <div className="job-detail-drawer__section-header">
                <h3>Related activity</h3>
                {canManageJobs && onClearActivity ? (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => onClearActivity(jobId)}>
                    Clear activity
                  </button>
                ) : null}
              </div>
              <ActivityLogPanel jobId={jobId} limit={8} compact />
            </section>
          </>
        )}
      </aside>
    </>
  );
}
